import { FEATURE_LABEL, featureName, type FeatureKey } from '@/constants/features';
/**
 * Mirrors `backend/src/config/permissions.ts`. Keep the two in step — the keys
 * are the contract between the Roles screen and every institute endpoint.
 *
 * Grouped for the checkbox list: an institute administrator ticking boxes should
 * see them arranged the way the product is, not as one flat column.
 */

export const PERMISSION_LABEL = {
  INSTITUTE_PROFILE_VIEW: 'See the institute profile',
  INSTITUTE_PROFILE_EDIT: 'Edit the institute profile',
  ROLE_VIEW: 'See roles',
  ROLE_MANAGE: 'Create, edit and delete roles',
  CLASS_VIEW: 'See classes and sections',
  CLASS_MANAGE: 'Set up classes, sections, subjects and fees',
  ADMISSION_CREATE: 'Register a new student',
  STUDENT_VIEW: 'See the student roll',
  STUDENT_MANAGE: 'Correct a student record, or remove one',
  STAFF_VIEW: 'See the staff list',
  STAFF_MANAGE: 'Add staff, change their role, or close an account',
  ALLOCATION_VIEW: 'See who teaches what',
  ALLOCATION_MANAGE: 'Allot classes and subjects to teachers',
  ATTENDANCE_VIEW: 'See the daily register',
  ATTENDANCE_MANAGE: 'Take and correct the daily register',
  STAFF_ATTENDANCE_VIEW: 'See staff punch-ins',
  STAFF_ATTENDANCE_APPROVE: 'Approve or refuse a staff punch-in',
  STAFF_LEAVE_VIEW: 'See staff leave applications',
  STAFF_LEAVE_MANAGE: 'Approve or reject staff leave',
  HOMEWORK_VIEW: 'See homework set for a class',
  HOMEWORK_MANAGE: 'Set homework and mark it',
  RECORDING_VIEW: 'See recorded lessons',
  RECORDING_MANAGE: 'Post and take down recorded lessons',
  TIMETABLE_VIEW: 'See the institute timetable',
  TIMETABLE_MANAGE: 'Build the institute week',
  ACTIVITY_VIEW: 'See class activities',
  ACTIVITY_MANAGE: 'Set class activities and mark them',
  EXAM_VIEW: 'See exam papers and marks',
  EXAM_MANAGE: 'Set exam papers and enter marks',
  HOLIDAY_VIEW: 'See the institute calendar',
  HOLIDAY_MANAGE: 'Declare and withdraw holidays',
  CALENDAR_VIEW: 'See the academic calendar',
  CALENDAR_MANAGE: 'Plan the academic calendar',
  FEE_VIEW: 'See the fee register and receipts',
  FEE_COLLECT: 'Take fees and issue receipts',
  FEE_MANAGE: 'Set what a student is charged, and grant concessions',
  FEE_REMIND: 'Remind their own class’s families that fees are due',
  PAYROLL_VIEW: 'See pay heads and what each member of staff is paid',
  PAYROLL_MANAGE: 'Set up pay heads, and set or revise salaries',
  INSTITUTE_NOTICE_MANAGE: 'Put up and take down institute notices',
  MESSAGE_SEND: 'Send an SMS or WhatsApp notification to the roll',
  SUPPORT_TICKET_MANAGE: 'Raise and follow up support tickets',
  DAILY_REPORT_VIEW: 'See the daily record of what was covered',
  DAILY_REPORT_MANAGE: 'Write the daily record for a class',
  DAILY_REPORT_REVIEW: 'Read every teacher’s daily record',
  NOTICE_VIEW: 'See a class notice board',
  NOTICE_MANAGE: 'Pin and take down class notices',
} as const;

export type Permission = keyof typeof PERMISSION_LABEL;

/** Extra line under each checkbox, so "Edit" is not a guess about scope. */
export const PERMISSION_HINT: Record<Permission, string> = {
  INSTITUTE_PROFILE_VIEW: 'Name, address, contact details and branding.',
  INSTITUTE_PROFILE_EDIT: 'Change any of the above. The institute code stays fixed.',
  ROLE_VIEW: 'See which roles exist and what each one allows.',
  ROLE_MANAGE: 'Effectively full control — a role can be given any permission.',
  CLASS_VIEW: 'The class roster: sections, seats, subjects and the yearly fee.',
  CLASS_MANAGE: 'Add and change classes, including what each one charges.',
  ADMISSION_CREATE: 'Take an admission: issues an admission number and opens the fee record.',
  STUDENT_VIEW: "Every child on this year's roll, and their record.",
  STUDENT_MANAGE: 'Correct details after admission, move a child, or take them off the roll.',
  STAFF_VIEW: 'Who works here, what each account may do, and who has yet to sign in.',
  STAFF_MANAGE:
    'Effectively full control — whoever holds this can open an account and give it any role.',
  ALLOCATION_VIEW:
    'The whole institute’s arrangement: which teacher takes each section and subject.',
  ALLOCATION_MANAGE:
    'Decides whose register a child appears on and whose console shows their marks.',
  ATTENDANCE_VIEW: 'A section’s register for a given day, and who took it.',
  ATTENDANCE_MANAGE:
    'Only for the sections they are class teacher of, unless they can also correct student records.',
  STAFF_ATTENDANCE_VIEW: 'Who signed in on a given day, and at what time.',
  STAFF_ATTENDANCE_APPROVE:
    'Decides whether a colleague was at work — a payroll fact. Nobody may approve their own.',
  STAFF_LEAVE_VIEW: 'Every colleague’s leave applications and what was decided.',
  STAFF_LEAVE_MANAGE:
    'Grants or refuses a colleague’s leave — a payroll fact. Nobody may decide their own.',
  HOMEWORK_VIEW: 'What has been set for a section, and who has handed it in.',
  HOMEWORK_MANAGE:
    'Only for the sections and subjects they are allotted, unless they can also correct student records.',
  RECORDING_VIEW: 'The lesson library for a section, and who has watched each one.',
  RECORDING_MANAGE:
    'Post a link to a recorded lesson, or take one down. Same allotment rule as homework.',
  TIMETABLE_VIEW: 'The periods an institute day runs to, and what is taught in each.',
  TIMETABLE_MANAGE:
    'Set the periods and place lessons in the week. Also excuses a colleague from a period.',
  ACTIVITY_VIEW: 'Projects, practicals and presentations set for a section, and who has handed in.',
  ACTIVITY_MANAGE:
    'Set a project and record marks against it. Same rule as homework — only their own allotted sections and subjects.',
  EXAM_VIEW: 'Papers set for a section, the marks on them, and the class averages.',
  EXAM_MANAGE:
    'Set a paper and enter its marks — what goes on a report card. Only their own allotted sections and subjects.',
  HOLIDAY_VIEW: 'The days the institute is closed this year. Given to every role by default.',
  HOLIDAY_MANAGE:
    'Closes the institute for a day or a term — and with it, which days count as somebody’s absence.',
  CALENDAR_VIEW: 'Exam weeks, PTMs, sports days and fee dates planned for the year.',
  CALENDAR_MANAGE: 'Add and change what is on the year planner. Declaring a holiday is separate.',
  FEE_VIEW:
    'What every family owes and has paid this session, and the day’s takings. One of the more sensitive things about a household — give it to the office, not the staff room.',
  FEE_COLLECT:
    'Stand at the counter: record a payment, issue its receipt, and cancel one entered wrongly.',
  FEE_MANAGE:
    'Decide what a student is charged and write off part of it. Deliberately separate from collecting — taking cash is not the same as waiving a term’s tuition.',
  FEE_REMIND:
    'For class teachers: see which families in their own class have fees overdue or due soon — never the amounts — and send them a reminder by SMS.',
  PAYROLL_VIEW:
    'Every colleague’s salary, its breakdown and its history. Among the most private things the school holds — give it to whoever runs payroll, not the staff room.',
  PAYROLL_MANAGE:
    'Add and change pay heads, and set, revise, correct or withdraw a salary. Every change is kept on record.',
  INSTITUTE_NOTICE_MANAGE:
    'The institute notice board — read by every member of staff and every student at once. Reading it needs no permission; only putting something up does.',
  MESSAGE_SEND:
    'Reaches people outside the product. A notification lands on every student’s handset, costs the institute money, and cannot be recalled.',
  SUPPORT_TICKET_MANAGE:
    'Ask our support team for help and read their replies. Covers every ticket your institute has raised, not only your own.',
  DAILY_REPORT_VIEW:
    'The daily record of what was covered. Today this reads only your own day; the list is built from your own timetable.',
  DAILY_REPORT_MANAGE:
    'Write the day’s record. Not permission to write into any class — the endpoint still requires the allotment.',
  DAILY_REPORT_REVIEW:
    'Read what every teacher wrote about their classes, with its attachments. Read only — a teacher’s own day notes stay private.',
  NOTICE_VIEW: 'A class notice board — what a teacher has pinned for their own sections.',
  NOTICE_MANAGE:
    'Pin and take down class notices. “All classes” means the poster’s own sections, never the whole institute — that is a notification.',
};

/**
 * The Roles screen's groups — one for each sidebar page, under the sidebar's
 * name, in the sidebar's order: the admin pages first, then the teacher's.
 *
 * Every group carries the page's `feature`, so it takes the feature's
 * catalogue name (`permissionGroupTitle`) exactly as the sidebar does, and is
 * left off at a school that does not have that page switched on. A key with no
 * page of its own lives in the page that uses it: who teaches what is set up
 * on Classes, and Leave Requests runs on the attendance keys. Pages with no
 * keys at all (Dashboard, Transport, Change password) have nothing to grant.
 *
 * The server still decides which feature owns a key — holidays, for one, are
 * withdrawn with the Academic Calendar there (`backend/src/config/features.ts`),
 * and are granted under it here, where the calendar page now keeps them.
 */
export interface PermissionGroup {
  key: string;
  /** The name when the catalogue gives none. */
  title: string;
  feature?: FeatureKey;
  permissions: Permission[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  // --- the admin sidebar ---
  {
    key: 'profile',
    title: FEATURE_LABEL.INSTITUTE_PROFILE,
    feature: 'INSTITUTE_PROFILE',
    permissions: ['INSTITUTE_PROFILE_VIEW', 'INSTITUTE_PROFILE_EDIT'],
  },
  {
    key: 'classes',
    title: FEATURE_LABEL.CLASSES,
    feature: 'CLASSES',
    permissions: ['CLASS_VIEW', 'ALLOCATION_VIEW', 'CLASS_MANAGE', 'ALLOCATION_MANAGE'],
  },
  { key: 'staff', title: FEATURE_LABEL.STAFF, feature: 'STAFF', permissions: ['STAFF_VIEW', 'STAFF_MANAGE'] },
  {
    key: 'timetable',
    title: FEATURE_LABEL.TIMETABLE,
    feature: 'TIMETABLE',
    permissions: ['TIMETABLE_VIEW', 'TIMETABLE_MANAGE'],
  },
  {
    key: 'students',
    title: FEATURE_LABEL.STUDENT_RECORDS,
    feature: 'STUDENT_RECORDS',
    permissions: ['STUDENT_VIEW', 'ADMISSION_CREATE', 'STUDENT_MANAGE'],
  },
  { key: 'fees', title: FEATURE_LABEL.FEES, feature: 'FEES', permissions: ['FEE_VIEW', 'FEE_COLLECT', 'FEE_MANAGE'] },
  {
    key: 'fee-reminders',
    title: FEATURE_LABEL.FEE_REMINDERS,
    feature: 'FEE_REMINDERS',
    permissions: ['FEE_REMIND'],
  },
  {
    key: 'payroll',
    title: FEATURE_LABEL.PAYROLL,
    feature: 'PAYROLL',
    permissions: ['PAYROLL_VIEW', 'PAYROLL_MANAGE'],
  },
  {
    key: 'staff-attendance',
    title: FEATURE_LABEL.STAFF_ATTENDANCE,
    feature: 'STAFF_ATTENDANCE',
    // Leave runs on the same feature on the server (`config/features.ts`).
    permissions: [
      'STAFF_ATTENDANCE_VIEW',
      'STAFF_ATTENDANCE_APPROVE',
      'STAFF_LEAVE_VIEW',
      'STAFF_LEAVE_MANAGE',
    ],
  },
  {
    key: 'calendar',
    title: FEATURE_LABEL.CALENDAR,
    feature: 'CALENDAR',
    permissions: ['CALENDAR_VIEW', 'CALENDAR_MANAGE', 'HOLIDAY_VIEW', 'HOLIDAY_MANAGE'],
  },
  {
    key: 'notice-board',
    title: FEATURE_LABEL.INSTITUTE_NOTICE,
    feature: 'INSTITUTE_NOTICE',
    permissions: ['INSTITUTE_NOTICE_MANAGE'],
  },
  { key: 'notifications', title: FEATURE_LABEL.MESSAGING, feature: 'MESSAGING', permissions: ['MESSAGE_SEND'] },
  { key: 'support', title: FEATURE_LABEL.SUPPORT, feature: 'SUPPORT', permissions: ['SUPPORT_TICKET_MANAGE'] },
  { key: 'roles', title: FEATURE_LABEL.ROLES, feature: 'ROLES', permissions: ['ROLE_VIEW', 'ROLE_MANAGE'] },
  // --- the teacher sidebar ---
  {
    key: 'attendance',
    title: FEATURE_LABEL.ATTENDANCE,
    feature: 'ATTENDANCE',
    permissions: ['ATTENDANCE_VIEW', 'ATTENDANCE_MANAGE'],
  },
  {
    key: 'homework',
    title: FEATURE_LABEL.HOMEWORK,
    feature: 'HOMEWORK',
    permissions: ['HOMEWORK_VIEW', 'HOMEWORK_MANAGE'],
  },
  {
    key: 'activities',
    title: FEATURE_LABEL.ACTIVITY,
    feature: 'ACTIVITY',
    permissions: ['ACTIVITY_VIEW', 'ACTIVITY_MANAGE'],
  },
  {
    key: 'recordings',
    title: FEATURE_LABEL.RECORDING,
    feature: 'RECORDING',
    permissions: ['RECORDING_VIEW', 'RECORDING_MANAGE'],
  },
  { key: 'exams', title: FEATURE_LABEL.EXAM, feature: 'EXAM', permissions: ['EXAM_VIEW', 'EXAM_MANAGE'] },
  {
    key: 'class-notices',
    title: FEATURE_LABEL.CLASS_NOTICE,
    feature: 'CLASS_NOTICE',
    permissions: ['NOTICE_VIEW', 'NOTICE_MANAGE'],
  },
  {
    key: 'daily-report',
    title: FEATURE_LABEL.REPORTS,
    feature: 'REPORTS',
    permissions: ['DAILY_REPORT_VIEW', 'DAILY_REPORT_MANAGE', 'DAILY_REPORT_REVIEW'],
  },
];

/** A group's name — the catalogue's, for a page that is a feature. */
export function permissionGroupTitle(
  group: PermissionGroup,
  featureNames: Partial<Record<FeatureKey, string>> | undefined,
): string {
  return group.feature ? featureName(group.feature, featureNames) : group.title;
}

/**
 * The groups a school can grant: every always-on group, and the pages it has
 * switched on. `features` undefined (platform staff) shows them all.
 */
export function permissionGroupsFor(features: readonly FeatureKey[] | undefined): PermissionGroup[] {
  if (!features) return PERMISSION_GROUPS;
  return PERMISSION_GROUPS.filter((group) => !group.feature || features.includes(group.feature));
}

export const ALL_PERMISSIONS = Object.keys(PERMISSION_LABEL) as Permission[];

/**
 * Kept on the Administrator role whatever is unticked — mirrors `ADMIN_ALWAYS`
 * in the API. Without them the administrator could never tick anything back.
 */
export const ADMIN_ALWAYS: readonly Permission[] = ['ROLE_VIEW', 'ROLE_MANAGE'];

/**
 * Keys only the Administrator role may carry — mirrors `ADMIN_ONLY` in the API,
 * which refuses them on any other role. The Staff and Roles pages decide who
 * has an account and what it may do; whoever holds them can grant the rest.
 */
export const ADMIN_ONLY: readonly Permission[] = [
  'STAFF_VIEW',
  'STAFF_MANAGE',
  'ROLE_VIEW',
  'ROLE_MANAGE',
];

/** What kind of account an institute role is for — mirrors `ROLE_KINDS` in the API. */
export const ROLE_KINDS = ['OFFICE', 'PRINCIPAL', 'TEACHER', 'ACCOUNTANT', 'LIBRARIAN', 'COUNSELLOR'] as const;
export type RoleKind = (typeof ROLE_KINDS)[number];

export const ROLE_KIND_LABEL: Record<RoleKind, string> = {
  OFFICE: 'School office',
  PRINCIPAL: 'Principal',
  TEACHER: 'Teacher',
  ACCOUNTANT: 'Accountant',
  LIBRARIAN: 'Librarian',
  COUNSELLOR: 'Counsellor',
};

export const ROLE_KIND_HINT: Record<RoleKind, string> = {
  OFFICE: 'Front office and administration. Every page except Staff and Roles.',
  PRINCIPAL: 'Runs the school. Everything except collecting fees and payroll.',
  TEACHER: 'Classroom staff. Teaching pages, and view only for the rest.',
  ACCOUNTANT: 'Fees, payroll and admissions, and view only for the rest.',
  LIBRARIAN: 'View only: classes, students, timetable and calendar.',
  COUNSELLOR: 'View only: classes, students, timetable and calendar.',
};

const READ_ONLY: readonly Permission[] = [
  'INSTITUTE_PROFILE_VIEW',
  'CLASS_VIEW',
  'ALLOCATION_VIEW',
  'STUDENT_VIEW',
  'TIMETABLE_VIEW',
  'HOLIDAY_VIEW',
  'CALENDAR_VIEW',
];

const CLASSROOM: readonly Permission[] = [
  'ATTENDANCE_VIEW',
  'ATTENDANCE_MANAGE',
  'HOMEWORK_VIEW',
  'HOMEWORK_MANAGE',
  'ACTIVITY_VIEW',
  'ACTIVITY_MANAGE',
  'RECORDING_VIEW',
  'RECORDING_MANAGE',
  'EXAM_VIEW',
  'EXAM_MANAGE',
  'NOTICE_VIEW',
  'NOTICE_MANAGE',
  'DAILY_REPORT_VIEW',
  'DAILY_REPORT_MANAGE',
  'FEE_REMIND',
];

const ACCOUNTS: readonly Permission[] = ['FEE_COLLECT', 'FEE_MANAGE', 'PAYROLL_VIEW', 'PAYROLL_MANAGE'];

/**
 * The keys each kind of role may hold — mirrors `ROLE_KIND_PERMISSIONS` in the
 * API, which refuses anything else. The Roles screen offers a role only these.
 */
export const ROLE_KIND_PERMISSIONS: Record<RoleKind, readonly Permission[]> = {
  OFFICE: ALL_PERMISSIONS.filter((key) => !ADMIN_ONLY.includes(key)),
  PRINCIPAL: ALL_PERMISSIONS.filter((key) => !ADMIN_ONLY.includes(key) && !ACCOUNTS.includes(key)),
  TEACHER: [...READ_ONLY, ...CLASSROOM],
  ACCOUNTANT: [...READ_ONLY, 'ADMISSION_CREATE', 'FEE_VIEW', ...ACCOUNTS],
  LIBRARIAN: READ_ONLY,
  COUNSELLOR: READ_ONLY,
};

/** Reads a permission off the signed-in user. Platform staff hold none of these. */
export function hasPermission(held: Permission[] | undefined, needed: Permission): boolean {
  return Boolean(held?.includes(needed));
}
